import { AUTH_RECOVERY_HEADER, showAuthError } from "./auth-recovery";
import { HttpError, isAuthRecoveryData } from "./http-error";

const DASHBOARD_CLIENT_HEADER = "Dashboard";

type QueryParamPrimitive = string | number | boolean | null | undefined;
type QueryParamValue = QueryParamPrimitive | QueryParamPrimitive[];

export type RequestQueryParams = Record<string, QueryParamValue>;

type ApiRequestConfig = {
  headers?: HeadersInit;
  params?: RequestQueryParams;
  signal?: AbortSignal;
};

type ApiResponse<T> = {
  data: T;
  status: number;
  headers: Headers;
};

type ApiRequestWithBody = ApiRequestConfig & {
  body?: unknown;
};

function applyQueryParams(url: URL, params?: RequestQueryParams) {
  if (!params) {
    return;
  }

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          url.searchParams.append(key, String(item));
        }
      }
      continue;
    }

    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof ReadableStream
  );
}

function resolveBody(body: unknown, headers: Headers): BodyInit | undefined {
  if (body === undefined) {
    return undefined;
  }

  if (isRawBody(body)) {
    return body;
  }

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return JSON.stringify(body);
}

function resolveUrl(
  baseURL: string,
  path: string,
  params?: RequestQueryParams
): URL {
  let url: URL;

  if (path === "") {
    url = new URL(baseURL);
  } else if (path.startsWith("http://") || path.startsWith("https://")) {
    url = new URL(path);
  } else {
    // Strip leading slash so new URL() resolves relative to the full baseURL
    // path, not the origin. "/workers" with baseURL ".../orgs/x/projects/y"
    // must resolve to ".../orgs/x/projects/y/workers", not ".../workers".
    const relative = path.startsWith("/") ? path.slice(1) : path;
    url = new URL(relative, baseURL.endsWith("/") ? baseURL : `${baseURL}/`);
  }

  applyQueryParams(url, params);
  return url;
}

async function parseResponseData(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();

  if (!text) {
    return undefined;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }

  return text;
}

async function isAuthRecoveryResponse(response: Response): Promise<boolean> {
  if (response.headers.get(AUTH_RECOVERY_HEADER)) {
    return true;
  }
  // Fall back to body inspection when the header isn't visible (CORS
  // misconfiguration, missing exposeHeaders, etc.) so the overlay still
  // opens for genuine auth-recovery 401s — without this, the route error
  // boundary would suppress the error UI on body shape while the http-client
  // didn't open the overlay, leaving a blank screen.
  if (response.status !== 401) {
    return false;
  }
  try {
    const data = (await response.clone().json()) as unknown;
    return isAuthRecoveryData(data);
  } catch {
    return false;
  }
}

/**
 * Sends a request and returns the raw `Response`.
 * Use this for streaming or integrations that need fetch-compatible behavior.
 */
export async function sendRequest(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      "Arcade-Client": DASHBOARD_CLIENT_HEADER,
      ...Object.fromEntries(new Headers(init?.headers)),
    },
  });
  if (await isAuthRecoveryResponse(response)) {
    showAuthError();
  }
  return response;
}

async function sendApiRequest<T>(
  baseURL: string,
  path: string,
  method: string,
  config?: ApiRequestWithBody
): Promise<ApiResponse<T>> {
  const headers = new Headers(config?.headers);
  const url = resolveUrl(baseURL, path, config?.params);
  const response = await sendRequest(url, {
    method,
    headers,
    signal: config?.signal,
    body: resolveBody(config?.body, headers),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw new HttpError(response, data);
  }

  return {
    data: data as T,
    status: response.status,
    headers: response.headers,
  };
}

export type ApiClient = {
  get: <T>(path: string, config?: ApiRequestConfig) => Promise<ApiResponse<T>>;
  post: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  put: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  patch: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  delete: <T>(
    path: string,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
};

export function createApiClient(args: {
  baseURL: string;
  defaultParams?: RequestQueryParams;
}): ApiClient {
  const { baseURL, defaultParams } = args;

  const withDefaults = (config?: ApiRequestConfig): ApiRequestWithBody => ({
    ...config,
    params: {
      ...defaultParams,
      ...config?.params,
    },
  });

  return {
    get: <T>(path: string, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "GET", withDefaults(config)),
    post: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "POST", {
        ...withDefaults(config),
        body,
      }),
    put: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "PUT", {
        ...withDefaults(config),
        body,
      }),
    patch: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "PATCH", {
        ...withDefaults(config),
        body,
      }),
    delete: <T>(path: string, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "DELETE", withDefaults(config)),
  };
}
